import { FC, useEffect, useState } from "react";
import { SessionPartConfig } from "@/mds/components/sessions/session-parts/session-part.types";
import {
  MemCommonMarkdown,
  OnClickMemItem,
  OnMemItemOut,
  OnMemItemOver,
  MemCommonEditorTheme,
  assetProxyModule,
} from "@mem-labs/common-editor";
import styled from "@emotion/styled";
import { ThemeType, useTheme } from "@/mds/themes";
import {
  AttachmentReference,
  buildAttachmentRefMap,
  resolveAttachmentRefsInMarkdown,
} from "./attachment-reference-utils";

type Props = {
  content: string;
  onClickMemItem: OnClickMemItem;
  config: SessionPartConfig;
  onMemItemOver?: OnMemItemOver;
  onMemItemOut?: OnMemItemOut;
  renderLinksAsChips?: boolean;
  attachmentReferences?: AttachmentReference[];
};

export const SessionMarkdownContent: FC<Props> = ({
  content,
  onClickMemItem,
  config,
  onMemItemOver,
  onMemItemOut,
  renderLinksAsChips,
  attachmentReferences,
}: Props) => {
  const { themeType } = useTheme();
  const [processedContent, setProcessedContent] = useState(content);

  // Resolve attachment reference URLs asynchronously via the existing asset proxy config
  useEffect(() => {
    const refMap = buildAttachmentRefMap(attachmentReferences);
    if (!refMap || !config.resolveAssetProxy) {
      setProcessedContent(content);
      return;
    }

    let cancelled = false;

    config
      .resolveAssetProxy()
      .then(proxyConfig => {
        if (cancelled) return;
        if (!proxyConfig) {
          setProcessedContent(content);
          return;
        }

        const resolved = resolveAttachmentRefsInMarkdown(content, refMap, originalUrl => {
          return (
            assetProxyModule.buildProxyUrl({
              originalUrl,
              config: proxyConfig,
            }) ?? null
          );
        });

        setProcessedContent(resolved);
      })
      .catch(() => {
        if (cancelled) return;
        setProcessedContent(content);
      });

    return () => {
      cancelled = true;
    };
  }, [content, attachmentReferences, config]);

  return (
    <MarkdownContent
      onMemItemOver={onMemItemOver}
      onMemItemOut={onMemItemOut}
      onClickMemItem={onClickMemItem}
      onClickLink={config.onClickLink}
      openLinksInNewTab={config.openLinksInNewTab}
      prohibitNativeCmdClick={config.prohibitNativeCmdClick}
      renderLinksAsChips={renderLinksAsChips}
      contentAlignment="start"
      uiTheme={
        themeType === ThemeType.Harvest
          ? MemCommonEditorTheme.Harvest
          : MemCommonEditorTheme.HarvestMoon
      }
    >
      {processedContent}
    </MarkdownContent>
  );
};

const MarkdownContent = styled(MemCommonMarkdown)(({ theme }) => ({
  width: "fit-content",
  color: theme.colors.text.primary,
}));
